// Custom form widgets used by the parties overrides (config fields of type 'custom').
import { createElement, useEffect, useState } from 'react';

import { SelectOption } from '../../api/client';
import { useAuth } from '../../components/AuthGate';
import { isSuperUser } from '../../auth/permissions';
import { CrudCustomFieldProps, CrudField } from '../../crud/types';
import { t, tLabel } from '../../i18n';
import { Combobox } from '../../workspace/Combobox';
import { fetchAdaptedOptions, rememberCurrentUser } from './helpers';

type RemoteSelectProps = CrudCustomFieldProps & {
  endpoint: string;
  multiple?: boolean;
  required?: boolean;
  hidden?: boolean;
};

// A searchable select backed by any legacy option endpoint (timezones,
// currencies, merchant_types…) through the options adapter.
export function RemoteSelectField({ field, value, error, onChange, endpoint, multiple, required }: RemoteSelectProps) {
  const [options, setOptions] = useState<SelectOption[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchAdaptedOptions(endpoint)
      .then((items) => alive && setOptions(items))
      .catch(() => alive && setOptions([]))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [endpoint]);
  const normalized = multiple ? (Array.isArray(value) ? value : []) : ((value as string | number | undefined) ?? '');
  return (
    <Combobox
      label={tLabel(field.label)}
      value={normalized as string | number | Array<string | number>}
      options={options}
      multiple={multiple}
      onChange={onChange}
      error={error}
      helperText={field.helperText ? tLabel(field.helperText) : undefined}
      required={required ?? field.required}
      loading={loading}
      disabled={field.readOnly}
    />
  );
}

/** Build a 'custom' field rendering a RemoteSelectField. */
export function remoteSelect(field: Omit<CrudField, 'type' | 'render'> & { endpoint: string; multiple?: boolean }): CrudField {
  const { endpoint, multiple, ...rest } = field;
  return {
    ...rest,
    type: 'custom',
    render: (props) => createElement(RemoteSelectField, { ...props, endpoint, multiple })
  };
}

// Expense approver (users/search?roles=admin). Legacy: hidden for super admins
// unless the expense is recurring; required for everyone else.
function ApproverField(props: CrudCustomFieldProps) {
  const { user } = useAuth();
  useEffect(() => {
    rememberCurrentUser(user);
  }, [user]);
  const superUser = isSuperUser(user);
  const recurring = props.form.recurring === true || props.form.recurring === 1 || props.form.recurring === '1';
  if (superUser && !recurring) return null;
  return createElement(RemoteSelectField, {
    ...props,
    endpoint: 'users/search?roles=admin',
    required: !superUser,
    field: { ...props.field, helperText: superUser ? undefined : t('parties.approverRequired') }
  });
}

export function approverField(field: Omit<CrudField, 'type' | 'render'>): CrudField {
  return { ...field, type: 'custom', render: (props) => createElement(ApproverField, props) };
}

// Invisible widget that records the signed-in user so normalizeBeforeSave
// (plain functions) can apply role-dependent rules.
function UserProbe() {
  const { user } = useAuth();
  useEffect(() => {
    rememberCurrentUser(user);
  }, [user]);
  return null;
}

export function userProbeField(name = '__user_probe'): CrudField {
  return { name, label: '', type: 'custom', render: () => createElement(UserProbe) };
}
